<template>
  <div class="statistics-page">
    <XNav :content="[{ t: 'pages.statistics' }]" />
    <h1>{{ $t("stats.pageTitle") }}</h1>
    <p class="subtitle-text">{{ $t("stats.pageSubtitle") }}</p>

    <div class="filters">
      <div class="filter">
        <label>{{ $t("common.season") }}</label>
        <select v-model="selectedSeason" @change="onFilterChange">
          <option value="All">{{ $t("stats.allSeasons") }}</option>
          <option v-for="s in seasons" :key="s._id" :value="s._id">{{ s.Name }}</option>
        </select>
      </div>
      <div class="filter">
        <label>{{ $t("common.league") }}</label>
        <select v-model="selectedLeague" @change="onFilterChange">
          <option value="All">{{ $t("stats.allLeagues") }}</option>
          <option v-for="l in leagues" :key="l._id" :value="l._id">{{ l.Name }}</option>
        </select>
      </div>
      <div class="filter">
        <label>{{ $t("common.team") }}</label>
        <select v-model="selectedTeam" @change="onFilterChange">
          <option value="All">{{ $t("stats.allTeams") }}</option>
          <option v-for="t in teams" :key="t._id" :value="t._id">{{ t.Name }}</option>
        </select>
      </div>
    </div>

    <div class="tabs">
      <button
        v-for="tab in tabList"
        :key="tab.key"
        class="tab"
        :class="{ active: activeTab === tab.key }"
        @click="switchTab(tab.key)"
      >
        {{ tab.label }}
      </button>
    </div>

    <table class="stats-table clickable">
      <thead>
        <tr>
          <th class="rank-col">#</th>
          <th class="name-col">{{ $t("common.name") }}</th>
          <th
            v-for="col in activeColumns"
            :key="col.field"
            class="stat-col"
            :class="{ sorted: sortBy === col.field }"
            @click="sort(col.field)"
          >
            {{ col.label }}
            <span class="sort-arrow" v-if="sortBy === col.field">{{ sortAsc ? '▲' : '▼' }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in stats" :key="index" @click="goPlayer(row._id)">
          <td class="rank-col">{{ page * pageSize + index + 1 }}</td>
          <td class="name-col">
            <span class="player-name">{{ row.First_Name }} {{ row.Last_Name }}</span>
          </td>
          <td v-for="col in activeColumns" :key="col.field" class="stat-col">
            {{ row[col.field] || 0 }}
          </td>
        </tr>
        <tr v-if="stats.length === 0">
          <td :colspan="2 + activeColumns.length" class="empty">{{ $t("stats.noResults") }}</td>
        </tr>
      </tbody>
    </table>

    <div class="pagination">
      <button @click="prevPage" :disabled="page === 0">{{ $t("stats.previous") }}</button>
      <span class="page-info">{{ $t("stats.pageLabel") }} {{ page + 1 }}</span>
      <button @click="nextPage" :disabled="stats.length < pageSize">{{ $t("stats.next") }}</button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import XNav from "@/components/XNav.vue";

export default {
  name: "Statistics",
  components: { XNav },
  metaInfo() {
    return {
      title: this.$t("seo_metadata.statistics.title"),
      meta: [
        { vmid: "description", name: "description", content: this.$t("seo_metadata.statistics.description") },
        { vmid: "keywords", name: "keywords", content: this.$t("seo_metadata.keywords") },
        { vmid: "og:title", property: "og:title", content: this.$t("seo_metadata.statistics.title") },
        { vmid: "og:description", property: "og:description", content: this.$t("seo_metadata.statistics.description") },
        { vmid: "og:url", property: "og:url", content: "https://kingsleague.ca/statistics" },
        { vmid: "og:type", property: "og:type", content: "website" },
        { vmid: "twitter:title", name: "twitter:title", content: this.$t("seo_metadata.statistics.title") },
        { vmid: "twitter:description", name: "twitter:description", content: this.$t("seo_metadata.statistics.description") }
      ],
      link: [{ rel: "canonical", href: "https://kingsleague.ca/statistics" }]
    };
  },
  data() {
    return {
      stats: [],
      sortBy: "Goals",
      sortAsc: false,
      page: 0,
      pageSize: 30,
      activeTab: "goals",
      leagues: [],
      seasons: [],
      teams: [],
      selectedLeague: "All",
      selectedSeason: "All",
      selectedTeam: "All",
    };
  },
  computed: {
    tabList() {
      return [
        { key: "goals", label: this.$t("stats.topScorers") },
        { key: "assists", label: this.$t("stats.topAssists") },
        { key: "discipline", label: this.$t("stats.discipline") },
      ];
    },
    activeColumns() {
      if (this.activeTab === "goals") {
        return [
          { field: "Goals", label: this.$t("common.goals") },
          { field: "Assists", label: this.$t("common.assists") },
          { field: "MatchesPlayed", label: this.$t("stats.gamesPlayed") },
        ];
      } else if (this.activeTab === "assists") {
        return [
          { field: "Assists", label: this.$t("common.assists") },
          { field: "Goals", label: this.$t("common.goals") },
          { field: "MatchesPlayed", label: this.$t("stats.gamesPlayed") },
        ];
      } else {
        return [
          { field: "YellowCards", label: this.$t("stats.yellowCards") },
          { field: "RedCards", label: this.$t("stats.redCards") },
          { field: "MatchesPlayed", label: this.$t("stats.gamesPlayed") },
        ];
      }
    },
  },
  mounted() {
    // Read filters from URL query params
    if (this.$route.query.league) this.selectedLeague = this.$route.query.league;
    if (this.$route.query.season) this.selectedSeason = this.$route.query.season;
    if (this.$route.query.team) this.selectedTeam = this.$route.query.team;
    if (this.$route.query.tab) this.activeTab = this.$route.query.tab;

    Promise.all([
      axios.get(this.$apiUrl + "/seasons").then((res) => { this.seasons = res.data; }),
      axios.get(this.$apiUrl + "/teams").then((res) => { this.teams = res.data; }),
      axios.get(this.$apiUrl + "/menu").then((res) => {
        for (let league of res.data) {
          for (let child of league.childLeagues) {
            for (let div of child.Divisions) {
              this.leagues.push({
                Name: `${league.Name} > ${child.Name} > ${div.Name}`,
                _id: div._id,
              });
            }
          }
        }
      }),
    ]).then(() => {
      this.setDefaultSort();
      this.update();
    });
  },
  methods: {
    setDefaultSort() {
      if (this.activeTab === "goals") this.sortBy = "Goals";
      else if (this.activeTab === "assists") this.sortBy = "Assists";
      else this.sortBy = "YellowCards";
      this.sortAsc = false;
    },
    switchTab(tab) {
      this.activeTab = tab;
      this.page = 0;
      this.setDefaultSort();
      this.updateQueryParams();
      this.update();
    },
    sort(key) {
      if (key === this.sortBy) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortBy = key;
        this.sortAsc = false;
      }
      this.page = 0;
      this.update();
    },
    onFilterChange() {
      this.page = 0;
      this.updateQueryParams();
      this.update();
    },
    updateQueryParams() {
      const query = {};
      if (this.selectedLeague !== "All") query.league = this.selectedLeague;
      if (this.selectedSeason !== "All") query.season = this.selectedSeason;
      if (this.selectedTeam !== "All") query.team = this.selectedTeam;
      if (this.activeTab !== "goals") query.tab = this.activeTab;
      this.$router.replace({ query }).catch(() => {});
    },
    nextPage() {
      if (this.stats.length >= this.pageSize) {
        this.page++;
        this.update();
      }
    },
    prevPage() {
      if (this.page > 0) {
        this.page--;
        this.update();
      }
    },
    update() {
      this.stats = [];
      axios
        .get(this.$apiUrl + "/statistics/players", {
          params: {
            leagueID: this.selectedLeague,
            seasonID: this.selectedSeason,
            teamID: this.selectedTeam,
            skipNumber: this.page * this.pageSize,
            limitNumber: this.pageSize,
            field: (this.sortAsc ? "" : "-") + this.sortBy,
          },
        })
        .then((res) => {
          this.stats = res.data;
        });
    },
    goPlayer(id) {
      this.$router.push({ path: `/players/${id}` });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/theme.scss";

.statistics-page {
  padding-top: 15px;
}

h1 {
  @extend %font-bold;
  font-size: 28px;
  margin-bottom: 5px;
}
.subtitle-text {
  color: #666;
  margin-bottom: 20px;
  font-size: 15px;
}

.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.filter {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 150px;

  label {
    font-size: 12px;
    text-transform: uppercase;
    color: #999;
    margin-bottom: 4px;
    letter-spacing: 0.5px;
  }
  select {
    padding: 8px 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 14px;
    font-family: inherit;
    background: white;
    &:focus {
      outline: none;
      border-color: $secondary;
    }
  }
}

.tabs {
  display: flex;
  border-bottom: 2px solid #eee;
  margin-bottom: 0;
}
.tab {
  padding: 10px 20px;
  border: none;
  background: none;
  font-size: 15px;
  cursor: pointer;
  color: #999;
  @extend %font-bold;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  transition: color 0.2s;

  &:hover {
    color: #333;
  }
  &.active {
    color: $secondary;
    border-bottom-color: $secondary;
  }
}

.stats-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background-color: $secondary;
    color: white;
    @extend %font-bold;
    user-select: none;
  }
  th {
    padding: 10px 12px;
    text-align: left;
    font-size: 14px;
  }
  td {
    padding: 10px 12px;
    font-size: 14px;
  }
  tbody tr {
    cursor: pointer;
    &:nth-child(odd) { background-color: #fff; }
    &:nth-child(even) { background-color: #f5f5f5; }
    &:hover { background-color: #eef2f7; }
  }
}

.rank-col {
  width: 40px;
  text-align: center;
  color: #999;
}
.stat-col {
  text-align: center;
  min-width: 50px;
  cursor: pointer;
}
th.stat-col {
  cursor: pointer;
  &:hover { color: $primary; }
}
th.sorted {
  color: $primary;
}
.sort-arrow {
  font-size: 10px;
  margin-left: 3px;
}
.player-name {
  font-size: 14px;
}
.empty {
  text-align: center;
  padding: 30px;
  color: #999;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin: 20px 0;

  button {
    background-color: $secondary;
    color: white;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
  .page-info {
    font-size: 14px;
    color: #666;
  }
}

@media only screen and (max-width: $mobile-breakpoint) {
  .filters {
    flex-direction: column;
  }
  .tabs {
    overflow-x: auto;
  }
  .tab {
    padding: 10px 12px;
    font-size: 13px;
    white-space: nowrap;
  }
  .name-col {
    min-width: 120px;
  }
}
</style>
